/**
 * PdplConsentCheckbox — shared PDPL consent gate (B-10).
 * Bilingual AR/EN; gates form submission until checked.
 */

export interface PdplConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  lang?: 'en' | 'ar';
  privacyUrl?: string;
  className?: string;
  disabled?: boolean;
  testId?: string;
}

const TEXT = {
  en: {
    label: 'I agree to the',
    linkText: 'Privacy Policy',
    suffix: 'and consent to the processing of my personal data per Saudi PDPL.',
  },
  ar: {
    label: 'أوافق على',
    linkText: 'سياسة الخصوصية',
    suffix: 'وأوافق على معالجة بياناتي الشخصية وفقاً لنظام حماية البيانات الشخصية.',
  },
} as const;

export function PdplConsentCheckbox({
  checked,
  onChange,
  lang = 'ar',
  privacyUrl = '/privacy-policy',
  className = '',
  disabled = false,
  testId = 'pdpl-consent-checkbox',
}: PdplConsentCheckboxProps) {
  const isAr = lang === 'ar';
  const T = TEXT[lang];
  return (
    <div className={`pdpl-consent ${className}`} dir={isAr ? 'rtl' : 'ltr'}>
      <label
        className="flex items-start gap-2 text-sm text-muted-foreground select-none"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => !disabled && onChange(e.target.checked)}
          disabled={disabled}
          aria-required="true"
          data-testid={testId}
          className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
        />
        <span className="leading-snug">
          {T.label}{' '}
          <a
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 font-medium hover:opacity-80"
            onClick={(e) => e.stopPropagation()}
          >
            {T.linkText}
          </a>{' '}
          {T.suffix}
        </span>
      </label>
    </div>
  );
}

/** Spread into POST body: `{ ...buildConsentPayload() }` */
export function buildConsentPayload() {
  return { consent_given: true, consent_timestamp: new Date().toISOString() };
}
